import React, { useState, useEffect, useRef } from 'react';
import './Orders.css';
import { getSiteLogoUrlFromStorage } from '../../utils/siteBranding';
import useOrders from '../hooks/useOrders';
import AdminLoading from './AdminLoading';
import useUpdateOrderStatus from '../hooks/useUpdateOrderStatus';
import { PLACEHOLDER_NO_IMAGE_TINY } from '../../utils/placeholderImage';
import { getPrimaryProductImageUrl } from '../../utils/productImages';
import { getAdminUser } from '../utils/authUtils';
const ORDER_STATUS_OPTIONS = [
  'Order Placed', 'Order Confirmed', 'Processing', 'Packed',
  'Dispatched', 'In Transit', 'Out for Delivery', 'Delivered', 'Cancelled'
];

const STATUS_MAP = {
  pending: 'Order Placed',
  confirmed: 'Order Confirmed',
  processing: 'Processing',
  packed: 'Packed',
  dispatched: 'Dispatched',
  'in transit': 'In Transit',
  in_transit: 'In Transit',
  'out for delivery': 'Out for Delivery',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  shipped: 'Dispatched'
};

const normalizeStatus = (s) => STATUS_MAP[String(s || '').toLowerCase()] || s || 'Order Placed';

/** Mongo/API id → string for admin API paths */
const toOrderIdString = (orderId) => {
  if (orderId == null || orderId === '') return '';
  if (typeof orderId === 'object') {
    if (orderId.$oid) return String(orderId.$oid);
    if (orderId._id != null) return toOrderIdString(orderId._id);
  }
  return String(orderId);
};

const Orders = ({ currentUser, navigationParams = {} }) => {
  const isTeamMember = currentUser?.role === 'team';
  const { orders, loading, error, refetch, getOrderById } = useOrders();
  const { updateOrderStatus, loading: statusUpdating, updatingOrderId } = useUpdateOrderStatus();
  const [statusUpdateError, setStatusUpdateError] = useState(null);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Order details modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState(null);
  const [activeTab, setActiveTab] = useState('customer');

  // Orders are now loaded automatically by the useOrders hook

  const openedFromNavRef = useRef(null);

  const buildNotifyContext = (order) => {
    if (!order || typeof order !== 'object') return null;
    const userEmail =
      order.email ||
      order.userEmail ||
      order.customerEmail ||
      order.user?.email ||
      order.customer?.email ||
      order.shippingAddress?.email ||
      order.deliveryAddress?.email;
    return {
      orderNumber: order.orderNumber,
      userEmail: userEmail && String(userEmail).trim() ? String(userEmail).trim() : undefined,
    };
  };

  const handleUpdateOrderStatus = async (order, newStatus) => {
    const orderId = order?._id || order?.id;
    if (!orderId) {
      setStatusUpdateError('Invalid order');
      setTimeout(() => setStatusUpdateError(null), 5000);
      return;
    }
    setStatusUpdateError(null);
    setStatusUpdateSuccess(null);
    try {
      await updateOrderStatus(orderId, newStatus, '', buildNotifyContext(order));
      setStatusUpdateSuccess('Order status updated successfully');
      setTimeout(() => setStatusUpdateSuccess(null), 3000);
      await refetch();
      if (selectedOrder && (selectedOrder._id === orderId || selectedOrder.id === orderId)) {
        setSelectedOrder(prev => prev ? { ...prev, status: newStatus } : null);
        setOrderDetails(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      setStatusUpdateError(err.message || 'Failed to update status');
      setTimeout(() => setStatusUpdateError(null), 5000);
    }
  };

  const viewOrderDetails = async (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
    setOrderDetails(null);
    setOrderDetailsError(null);
    setOrderDetailsLoading(true);

    try {
      // Fetch full order details from API
      const orderId = order._id || order.id;
      const fullOrderDetails = await getOrderById(orderId);
      setOrderDetails(fullOrderDetails);
      setOrderDetailsError(null);
    } catch (err) {
      console.error('Error fetching order details:', err);
      // Fallback: use order from list (backend already filtered for team members)
      setOrderDetails(order);
      // Only show error if order from list is empty/incomplete
      setOrderDetailsError(order?.items?.length ? null : (err.message || 'Showing basic order info'));
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  useEffect(() => {
    const oid = navigationParams?.openOrderId;
    if (!oid) {
      openedFromNavRef.current = null;
      return;
    }
    if (loading || !orders?.length) return;
    if (openedFromNavRef.current === oid) return;
    const match = orders.find((o) => String(o._id || o.id) === String(oid));
    if (match) {
      openedFromNavRef.current = oid;
      viewOrderDetails(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationParams?.openOrderId, orders, loading]);

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
    setOrderDetails(null);
    setOrderDetailsError(null);
    setActiveTab('customer');
  };

  const handleDeleteOrder = async (order) => {
    const orderId = toOrderIdString(order._id || order.id);
    if (!orderId) {
      setStatusUpdateError('Invalid order id');
      setTimeout(() => setStatusUpdateError(null), 5000);
      return;
    }
    if (!window.confirm(`Cancel/Delete order #${order.orderNumber || orderId}? This will set the order status to Cancelled.`)) return;
    setDeletingOrderId(orderId);
    try {
      // Use status update (admin), not POST /cancel — backend cancel is often limited to early stages only
      await updateOrderStatus(orderId, 'Cancelled', 'Cancelled by admin', buildNotifyContext(order));
      setStatusUpdateSuccess('Order cancelled successfully');
      setTimeout(() => setStatusUpdateSuccess(null), 3000);
      await refetch();
      if (
        selectedOrder &&
        toOrderIdString(selectedOrder._id || selectedOrder.id) === orderId
      ) {
        closeOrderModal();
      }
    } catch (err) {
      console.error('Delete order error:', err);
      setStatusUpdateError(err.message || 'Failed to cancel order');
      setTimeout(() => setStatusUpdateError(null), 5000);
    } finally {
      setDeletingOrderId(null);
    }
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  const getSellerAddressForInvoice = () => {
    try {
      const admin = getAdminUser() || currentUser || {};
      const uid = admin?._id || admin?.id || admin?.email || 'unknown';
      const scopedKey = `sheenwear_team_member_${uid}_seller_address`;
      const scopedStored = localStorage.getItem(scopedKey);
      const legacyStored = localStorage.getItem('sheenwear_seller_address');
      const raw = scopedStored || legacyStored;
      if (raw) {
        const addr = JSON.parse(raw);
        const name = addr.businessName || 'Super Wear';
        const lines = [addr.addressLine1, addr.addressLine2].filter(Boolean).join('<br>');
        const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
        const loc = [cityState, addr.postalCode].filter(Boolean).join(' ');
        const parts = [lines, loc, addr.country || 'Pakistan', `Phone: ${addr.phone || ''}`, `Email: ${addr.email || ''}`].filter(Boolean);
        return { name, html: parts.join('<br>') };
      }
    } catch (e) {}
    return { name: 'Super Wear', html: '123 Fashion Street<br>Karachi, Pakistan<br>Phone: +92 300 1234567<br>Email: info@superwear.com' };
  };

  const handlePrintInvoice = () => {
    const order = orderDetails || selectedOrder;
    if (!order) return;

    const sellerAddr = getSellerAddressForInvoice();
    const invoiceLogoUrl = getSiteLogoUrlFromStorage().replace(/"/g, '');

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Get order data
    const orderNumber = order.orderNumber || order.id || 'N/A';
    const orderDate = formatDate(order.createdAt || order.date);
    const customerName = order.user?.name || order.customer || order.customerName || 'N/A';
    const customerEmail = order.user?.email || order.email || 'N/A';
    const customerPhone = order.user?.phone || order.phone || order.shippingAddress?.phone || 'N/A';
    const shippingAddress = order.shippingAddress || order.address;
    const billingAddress = order.billingAddress || shippingAddress;
    const items = order.items || [];
    const { subtotal, shipping, couponDiscount, total } = calculateOrderFinancials(order);
    const paymentMethod = order.paymentMethod || 'N/A';
    const paymentStatus = order.paymentStatus || 'Pending';

    // Create invoice HTML
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${orderNumber}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 40px;
            color: #333;
          }
          .invoice-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #667eea;
          }
          .invoice-logo {
            max-height: 64px;
            max-width: 240px;
            object-fit: contain;
            margin-bottom: 12px;
            display: block;
          }
          .company-info h1 {
            color: #667eea;
            font-size: 28px;
            margin-bottom: 10px;
          }
          .company-info p {
            color: #666;
            font-size: 14px;
            line-height: 1.6;
          }
          .invoice-info {
            text-align: right;
          }
          .invoice-info h2 {
            color: #333;
            font-size: 24px;
            margin-bottom: 10px;
          }
          .invoice-info p {
            color: #666;
            font-size: 14px;
            margin: 5px 0;
          }
          .invoice-details {
            margin-bottom: 30px;
          }
          .detail-section {
            max-width: 400px;
          }
          .detail-section h3 {
            color: #667eea;
            font-size: 18px;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e9ecef;
          }
          .detail-section p {
            color: #666;
            font-size: 14px;
            margin: 4px 0;
            line-height: 1.4;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          .items-table th {
            background: #667eea;
            color: white;
            padding: 15px;
            text-align: left;
            font-weight: 600;
          }
          .items-table td {
            padding: 12px 15px;
            border-bottom: 1px solid #e9ecef;
          }
          .items-table tr:hover {
            background: #f8f9fa;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .totals-section {
            margin-left: auto;
            width: 300px;
            margin-top: 20px;
          }
          .totals-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px solid #e9ecef;
          }
          .totals-row.total {
            border-top: 2px solid #667eea;
            border-bottom: none;
            margin-top: 10px;
            padding-top: 15px;
            font-size: 20px;
            font-weight: 700;
            color: #667eea;
          }
          .footer {
            margin-top: 50px;
            padding-top: 20px;
            border-top: 2px solid #e9ecef;
            text-align: center;
            color: #666;
            font-size: 12px;
          }
          @media print {
            body {
              padding: 20px;
            }
            .print-btn {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          <div class="company-info">
            ${invoiceLogoUrl ? `<img src="${invoiceLogoUrl}" alt="" class="invoice-logo" />` : ''}
            <h1>${sellerAddr.name}</h1>
            <p>${sellerAddr.html}</p>
          </div>
          <div class="invoice-info">
            <h2>INVOICE</h2>
            <p><strong>Invoice #:</strong> ${orderNumber}</p>
            <p><strong>Date:</strong> ${orderDate}</p>
            <p><strong>Status:</strong> ${order.status || 'Pending'}</p>
          </div>
        </div>

        <div class="invoice-details">
          <div class="detail-section">
            <h3>Ship To:</h3>
            <p><strong>${customerName}</strong></p>
            ${shippingAddress ? `
              <p>${shippingAddress.addressLine1 || shippingAddress.address || ''}</p>
              ${shippingAddress.addressLine2 ? `<p>${shippingAddress.addressLine2}</p>` : ''}
              <p>${shippingAddress.city || ''}${shippingAddress.state ? `, ${shippingAddress.state}` : ''} ${shippingAddress.postalCode || ''}</p>
              <p>${shippingAddress.country || 'Pakistan'}</p>
              <p>Phone: ${shippingAddress.phone || customerPhone}</p>
            ` : `<p>No shipping address available</p><p>Phone: ${customerPhone}</p>`}
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th class="text-center">Quantity</th>
              <th class="text-right">Unit Price</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((item, index) => {
              const itemName = item.name || item.product?.name || item.productName || 'Unknown Product';
              const quantity = item.quantity || 1;
              const price = item.price || 0;
              const total = price * quantity;
              return `
                <tr>
                  <td>
                    <strong>${itemName}</strong><br>
                    <small>${item.size ? `Size: ${item.size}` : ''} ${item.color ? `Color: ${item.color}` : ''}</small>
                  </td>
                  <td class="text-center">${quantity}</td>
                  <td class="text-right">Rs. ${price.toLocaleString()}</td>
                  <td class="text-right">Rs. ${total.toLocaleString()}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="totals-section">
          <div class="totals-row">
            <span>Items Subtotal:</span>
            <span>Rs. ${subtotal.toLocaleString()}</span>
          </div>
          <div class="totals-row">
            <span>Shipping:</span>
            <span>${shipping > 0 ? `Rs. ${shipping.toLocaleString()}` : 'FREE'}</span>
          </div>
          ${couponDiscount > 0 ? `
            <div class="totals-row">
              <span>Coupon Discount:</span>
              <span>-Rs. ${couponDiscount.toLocaleString()}</span>
            </div>
          ` : ''}
          <div class="totals-row total">
            <span>Total:</span>
            <span>Rs. ${total.toLocaleString()}</span>
          </div>
        </div>

        <div class="footer">
          <p>Thank you for your business!</p>
          <p>This is a computer-generated invoice and does not require a signature.</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
  };

  const calculateOrderFinancials = (rawOrder) => {
    const toAmount = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const order = rawOrder || {};
    const items = Array.isArray(order.items) ? order.items : [];
    const itemsSubtotalFromLines = items.reduce((sum, item) => {
      const qty = toAmount(item?.quantity || 1);
      const lineTotal = toAmount(item?.total);
      if (lineTotal > 0) return sum + lineTotal;
      return sum + (toAmount(item?.price) * qty);
    }, 0);

    const subtotal = toAmount(order.subtotal) || itemsSubtotalFromLines;
    const shipping = toAmount(
      order.shipping ??
      order.shippingFee ??
      order.deliveryFee ??
      order.shippingCost
    );
    const couponDiscount = Math.max(
      0,
      toAmount(
        order.discount ??
        order.couponDiscount ??
        order.promoDiscount ??
        order.discountAmount
      )
    );
    const total = toAmount(order.total) || Math.max(0, subtotal + shipping - couponDiscount);
    return { subtotal, shipping, couponDiscount, total };
  };

  const getFilteredOrders = () => {
    let filtered = orders;

    // Backend already filters orders for team members (GET /api/admin/orders returns only their orders)
    // No frontend filter needed - item.product may not be populated, causing false filtering

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      
      switch (dateFilter) {
        case 'today':
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.createdAt || order.date);
            return orderDate.toDateString() === now.toDateString();
          });
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.createdAt || order.date);
            return orderDate >= weekAgo;
          });
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.createdAt || order.date);
            return orderDate >= monthAgo;
          });
          break;
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order => 
        order.orderNumber?.toLowerCase().includes(query) ||
        (order.user?.name || order.customer || order.customerName || '').toLowerCase().includes(query) ||
        (order.user?.email || order.email || '').toLowerCase().includes(query) ||
        (order.user?.phone || order.phone || '').toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Order Placed': '#10b981',
      'Order Confirmed': '#10b981',
      'Processing': '#3b82f6',
      'Packed': '#3b82f6',
      'Dispatched': '#f59e0b',
      'In Transit': '#f59e0b',
      'Out for Delivery': '#f59e0b',
      'Delivered': '#10b981',
      'Cancelled': '#ef4444',
      // Legacy support
      'pending': '#ffc107',
      'confirmed': '#10b981',
      'processing': '#3b82f6',
      'shipped': '#6f42c1',
      'delivered': '#28a745',
      'cancelled': '#dc3545',
      'refunded': '#6c757d'
    };
    return colors[status] || '#6c757d';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Order Placed': 'fas fa-check-circle',
      'Order Confirmed': 'fas fa-clipboard-check',
      'Processing': 'fas fa-cogs',
      'Packed': 'fas fa-box',
      'Dispatched': 'fas fa-shipping-fast',
      'In Transit': 'fas fa-truck',
      'Out for Delivery': 'fas fa-route',
      'Delivered': 'fas fa-home',
      'Cancelled': 'fas fa-times-circle',
      // Legacy support
      'pending': 'fas fa-clock',
      'confirmed': 'fas fa-clipboard-check',
      'processing': 'fas fa-cog',
      'shipped': 'fas fa-shipping-fast',
      'delivered': 'fas fa-check-circle',
      'cancelled': 'fas fa-times-circle',
      'refunded': 'fas fa-undo'
    };
    return icons[status] || 'fas fa-question-circle';
  };

  const formatCurrency = (amount) => {
    return `Rs. ${amount?.toLocaleString() || '0'}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && orders.length === 0) {
    return (
      <AdminLoading
        title="Loading Orders"
        subtitle="Fetching orders from database..."
        icon="🛒"
      />
    );
  }

  const filteredOrders = getFilteredOrders();
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const startIndex = (currentPage - 1) * ordersPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + ordersPerPage);

  return (
    <div className="orders-container">
      <div className="orders-header">
        <div className="header-content">
          <h1>{isTeamMember ? 'Your Orders' : 'Orders Management'}</h1>
          <p>{isTeamMember ? 'Orders containing your products' : 'Manage and track customer orders'}</p>
        </div>
        <div className="header-actions">
          <button onClick={refetch} className="refresh-btn" disabled={loading}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      {statusUpdateSuccess && (
        <div className="status-update-toast success">
          <i className="fas fa-check-circle"></i>
          {statusUpdateSuccess}
        </div>
      )}
      {statusUpdateError && (
        <div className="status-update-toast error">
          <i className="fas fa-exclamation-circle"></i>
          {statusUpdateError}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="Order Placed">Order Placed</option>
              <option value="Order Confirmed">Order Confirmed</option>
              <option value="Processing">Processing</option>
              <option value="Packed">Packed</option>
              <option value="Dispatched">Dispatched</option>
              <option value="In Transit">In Transit</option>
              <option value="Out for Delivery">Out for Delivery</option>
              <option value="Delivered">Delivered</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Date Range</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Search</label>
            <div className="search-input-group">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search by order number, customer, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchQuery('')}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="filter-stats">
          <div className="stat-item">
            <span className="stat-label">Total Orders:</span>
            <span className="stat-value">{filteredOrders.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Pending:</span>
            <span className="stat-value">{orders.filter(o => o.status === 'pending').length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Confirmed:</span>
            <span className="stat-value">{orders.filter(o => o.status === 'confirmed').length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Processing:</span>
            <span className="stat-value">{orders.filter(o => o.status === 'processing').length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Delivered:</span>
            <span className="stat-value">{orders.filter(o => o.status === 'delivered').length}</span>
          </div>
        </div>
      </div>

      {/* Orders Table or Empty State */}
      <div className="orders-table-container">
        {filteredOrders.length === 0 ? (
          <div className="orders-empty-state">
            <div className="empty-icon">🛒</div>
            <h3>{isTeamMember ? "No orders with your products yet" : 'No orders found'}</h3>
            <p>{isTeamMember ? 'Orders containing your products will appear here when customers purchase them.' : 'Orders will appear here when customers place them.'}</p>
          </div>
        ) : (
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order Details</th>
                <th>Customer</th>
                <th>Products</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map(order => (
                <tr key={order.id || order._id} className="order-row">
                  <td>
                    <div className="order-details">
                      <div className="order-number">{order.orderNumber || order.id}</div>
                      <div className="order-id">ID: {order.id || order._id}</div>
                    </div>
                  </td>
                  <td>
                    <div className="customer-details">
                      <div className="customer-name">{order.user?.name || order.customer || order.customerName || 'N/A'}</div>
                      <div className="customer-email">{order.user?.email || order.email || 'N/A'}</div>
                      <div className="customer-phone">{order.user?.phone || order.phone || 'N/A'}</div>
                    </div>
                  </td>
                  <td>
                    <div className="products-info">
                      <div className="products-count">
                        <i className="fas fa-box"></i>
                        {order.items?.length || order.products?.length || 0} items
                      </div>
                      {order.items && order.items.length > 0 && (
                        <div className="products-preview">
                          {order.items.slice(0, 2).map((item, index) => (
                            <span key={index} className="product-name">
                              {item.name || item.productName}
                            </span>
                          ))}
                          {order.items.length > 2 && (
                            <span className="more-items">+{order.items.length - 2} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="order-total">
                      {formatCurrency(order.total || order.amount)}
                    </div>
                  </td>
                  <td>
                    <div className="status-cell">
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                      >
                        <i className={getStatusIcon(order.status)}></i>
                        {order.status}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="date-cell">
                      {formatDate(order.createdAt || order.date)}
                    </div>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <div className="orders-table-action-group">
                        <button
                          type="button"
                          className="orders-table-action orders-table-action--view"
                          onClick={() => viewOrderDetails(order)}
                          title="View order details"
                        >
                          <i className="fas fa-eye" aria-hidden />
                          <span>View</span>
                        </button>
                        <button
                          type="button"
                          className="orders-table-action orders-table-action--delete"
                          onClick={() => handleDeleteOrder(order)}
                          disabled={deletingOrderId === (order._id || order.id)}
                          title="Cancel or delete order"
                        >
                          {deletingOrderId === (order._id || order.id) ? (
                            <i className="fas fa-spinner fa-spin" aria-hidden />
                          ) : (
                            <i className="fas fa-trash-alt" aria-hidden />
                          )}
                          <span>Delete</span>
                        </button>
                      </div>
                      <div className="status-update-wrapper">
                        <select
                          className="status-select"
                          value={normalizeStatus(order.status)}
                          onChange={(e) => handleUpdateOrderStatus(order, e.target.value)}
                          disabled={statusUpdating && updatingOrderId === (order._id || order.id)}
                          title="Change order status"
                        >
                          {ORDER_STATUS_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        {statusUpdating && updatingOrderId === (order._id || order.id) && (
                          <span className="status-update-spinner">⏳</span>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <div className="pagination-info">
            Showing {startIndex + 1} to {Math.min(startIndex + ordersPerPage, filteredOrders.length)} of {filteredOrders.length} orders
          </div>
          <div className="pagination-buttons">
            <button 
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <i className="fas fa-chevron-left"></i>
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`pagination-btn ${page === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            
            <button 
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Order Details Modal - Redesigned */}
      {showOrderModal && selectedOrder && (
        <div className="order-detail-modal-overlay" onClick={closeOrderModal}>
          <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="order-detail-header">
              <div className="order-detail-header-left">
                <div className="order-number-large">
                  <i className="fas fa-receipt"></i>
                  <span>Order #{orderDetails?.orderNumber || selectedOrder.orderNumber || selectedOrder.id}</span>
                </div>
                <div className="order-date-header">
                  <i className="fas fa-calendar"></i>
                  <span>{formatDate(orderDetails?.createdAt || selectedOrder.createdAt || selectedOrder.date)}</span>
                </div>
              </div>
              <div className="order-detail-header-right">
                <div className="order-detail-status-update">
                  <label className="status-update-label">Status:</label>
                  <select
                    className="status-select-modal"
                    value={normalizeStatus(orderDetails?.status || selectedOrder?.status)}
                    onChange={(e) =>
                      handleUpdateOrderStatus(
                        { ...selectedOrder, ...(orderDetails || {}) },
                        e.target.value
                      )
                    }
                    disabled={statusUpdating}
                    title="Change order status"
                  >
                    {ORDER_STATUS_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {statusUpdating && <span className="status-update-spinner">⏳</span>}
                </div>
                <button 
                  className="order-detail-delete-btn"
                  onClick={() => handleDeleteOrder(selectedOrder)}
                  disabled={deletingOrderId === (selectedOrder._id || selectedOrder.id)}
                  title="Cancel/Delete this order"
                >
                  {deletingOrderId === (selectedOrder._id || selectedOrder.id) ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fas fa-trash-alt"></i>
                  )}
                  <span>Delete</span>
                </button>
                <button className="order-detail-close-btn" onClick={closeOrderModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="order-detail-body">
              {orderDetailsLoading ? (
                <div className="order-detail-loading">
                  <div className="loading-spinner-large">
                    <div className="spinner"></div>
                    <p>Loading order details...</p>
                  </div>
                </div>
              ) : orderDetailsError ? (
                <div className="order-detail-error">
                  <i className="fas fa-exclamation-triangle"></i>
                  <p>{orderDetailsError}</p>
                  <button onClick={() => viewOrderDetails(selectedOrder)} className="retry-btn">
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  {/* Tabs Section */}
                  <div className="order-detail-tabs">
                    <div 
                      className={`order-detail-tab ${activeTab === 'customer' ? 'active' : ''}`}
                      onClick={() => handleTabChange('customer')}
                    >
                      <i className="fas fa-info-circle"></i>
                      <span>All Info</span>
                    </div>
                    <div 
                      className={`order-detail-tab ${activeTab === 'shipping' ? 'active' : ''}`}
                      onClick={() => handleTabChange('shipping')}
                    >
                      <i className="fas fa-truck"></i>
                      <span>Shipping & Payment</span>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="order-detail-content">
                    {/* All Info Tab (Products + Customer Info) */}
                    <div className={`tab-content ${activeTab === 'customer' ? 'active' : ''}`} id="customer-tab">
                      {/* Products Section */}
                      <div className="order-products-list">
                        {(orderDetails?.items || selectedOrder.items || []).map((item, index) => {
                          const productImage =
                            item.image ||
                            getPrimaryProductImageUrl(item.product) ||
                            PLACEHOLDER_NO_IMAGE_TINY;
                          const productName = item.name || item.product?.name || item.productName || 'Unknown Product';
                          const productPrice = item.price || 0;
                          const productQuantity = item.quantity || 1;
                          const productTotal = productPrice * productQuantity;
                          
                          return (
                            <div key={index} className="order-product-card">
                              <div className="product-card-image">
                                <img src={productImage} alt={productName} />
                              </div>
                              <div className="product-card-info">
                                <h4 className="product-card-name">{productName}</h4>
                                <div className="product-card-details">
                                  <span className="detail-item">
                                    <i className="fas fa-hashtag"></i>
                                    Product ID: {item.product?._id || item.product?.id || item.product || 'N/A'}
                                  </span>
                                  {item.size && (
                                    <span className="detail-item">
                                      <i className="fas fa-ruler"></i>
                                      Size: {item.size}
                                    </span>
                                  )}
                                  {item.color && (
                                    <span className="detail-item">
                                      <i className="fas fa-palette"></i>
                                      Color: {item.color}
                                    </span>
                                  )}
                                  <span className="detail-item">
                                    <i className="fas fa-shopping-cart"></i>
                                    Quantity: {productQuantity}
                                  </span>
                                </div>
                              </div>
                              <div className="product-card-pricing">
                                <div className="product-unit-price">
                                  {formatCurrency(productPrice)} <span className="per-unit">each</span>
                                </div>
                                <div className="product-total-price">
                                  {formatCurrency(productTotal)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Order Totals */}
                      <div className="order-totals-section">
                        <div className="order-totals-card">
                          <h4>Order Summary</h4>
                          {(() => {
                            const financials = calculateOrderFinancials(orderDetails || selectedOrder || {});
                            return (
                              <>
                                <div className="totals-row">
                                  <span className="totals-label">Items Subtotal:</span>
                                  <span className="totals-value">
                                    {formatCurrency(financials.subtotal)}
                                  </span>
                                </div>
                                <div className="totals-row">
                                  <span className="totals-label">Shipping:</span>
                                  <span className="totals-value">
                                    {financials.shipping > 0 ? formatCurrency(financials.shipping) : 'FREE'}
                                  </span>
                                </div>
                                {financials.couponDiscount > 0 && (
                                  <div className="totals-row">
                                    <span className="totals-label">Coupon Discount:</span>
                                    <span className="totals-value">
                                      -{formatCurrency(financials.couponDiscount)}
                                    </span>
                                  </div>
                                )}
                                <div className="totals-row totals-row-total">
                                  <span className="totals-label">Total:</span>
                                  <span className="totals-value">
                                    {formatCurrency(financials.total)}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Customer Information Section */}
                      <div className="info-section-card">
                        <h4><i className="fas fa-user-circle"></i> Customer Information</h4>
                        <div className="info-grid">
                          <div className="info-row">
                            <span className="info-label">Name:</span>
                            <span className="info-value">
                              {orderDetails?.user?.name || orderDetails?.customer || selectedOrder.customer || selectedOrder.customerName || 'N/A'}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Email:</span>
                            <span className="info-value">
                              {orderDetails?.user?.email || orderDetails?.email || selectedOrder.email || 'N/A'}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Phone:</span>
                            <span className="info-value">
                              {orderDetails?.user?.phone || orderDetails?.phone || selectedOrder.phone || orderDetails?.shippingAddress?.phone || 'N/A'}
                            </span>
                          </div>
                          {orderDetails?.user?._id && (
                            <div className="info-row">
                              <span className="info-label">User ID:</span>
                              <span className="info-value">{orderDetails.user._id}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Shipping & Payment Tab */}
                    <div className={`tab-content ${activeTab === 'shipping' ? 'active' : ''}`} id="shipping-tab">
                      <div className="addresses-grid">
                        <div className="address-card">
                          <h4><i className="fas fa-truck"></i> Shipping Address</h4>
                          {orderDetails?.shippingAddress ? (
                            <div className="address-details">
                              <p><strong>{orderDetails.shippingAddress.addressLine1}</strong></p>
                              {orderDetails.shippingAddress.addressLine2 && (
                                <p>{orderDetails.shippingAddress.addressLine2}</p>
                              )}
                              <p>
                                {orderDetails.shippingAddress.city}
                                {orderDetails.shippingAddress.state && `, ${orderDetails.shippingAddress.state}`}
                                {orderDetails.shippingAddress.postalCode && ` ${orderDetails.shippingAddress.postalCode}`}
                              </p>
                              <p>{orderDetails.shippingAddress.country || 'Pakistan'}</p>
                              {orderDetails.shippingAddress.phone && (
                                <p><i className="fas fa-phone"></i> {orderDetails.shippingAddress.phone}</p>
                              )}
                            </div>
                          ) : selectedOrder.address ? (
                            <div className="address-details">
                              {typeof selectedOrder.address === 'string' ? (
                                <p>{selectedOrder.address}</p>
                              ) : (
                                <>
                                  <p><strong>{selectedOrder.address.address || selectedOrder.address.addressLine1}</strong></p>
                                  <p>{selectedOrder.address.city}</p>
                                </>
                              )}
                            </div>
                          ) : (
                            <p className="no-data">No shipping address available</p>
                          )}
                        </div>

                        {/* Payment Information Card */}
                        <div className="info-section-card">
                          <h4><i className="fas fa-credit-card"></i> Payment Information</h4>
                          <div className="info-grid">
                            <div className="info-row">
                              <span className="info-label">Payment Method:</span>
                              <span className="info-value">
                                {orderDetails?.paymentMethod || selectedOrder.paymentMethod || 'N/A'}
                              </span>
                            </div>
                            <div className="info-row">
                              <span className="info-label">Payment Status:</span>
                              <span className="info-value">
                                <span className={`payment-status ${(orderDetails?.paymentStatus || selectedOrder.paymentStatus || 'pending').toLowerCase()}`}>
                                  {orderDetails?.paymentStatus || selectedOrder.paymentStatus || 'Pending'}
                                </span>
                              </span>
                            </div>
                            <div className="info-row">
                              <span className="info-label">Total Amount:</span>
                              <span className="info-value">
                                {formatCurrency(orderDetails?.total || selectedOrder.total || selectedOrder.amount)}
                              </span>
                            </div>
                            {orderDetails?.paidAt && (
                              <div className="info-row">
                                <span className="info-label">Paid At:</span>
                                <span className="info-value">{formatDate(orderDetails.paidAt)}</span>
                              </div>
                            )}
                            {orderDetails?.transactionId && (
                              <div className="info-row">
                                <span className="info-label">Transaction ID:</span>
                                <span className="info-value">{orderDetails.transactionId}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Print Invoice Button */}
                      <div className="print-invoice-section">
                        <button 
                          className="print-invoice-btn"
                          onClick={() => handlePrintInvoice()}
                        >
                          <i className="fas fa-print"></i>
                          <span>Print Invoice</span>
                        </button>
                      </div>
                    </div>

                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="order-detail-footer">
              <button className="btn-secondary" onClick={closeOrderModal}>
                <i className="fas fa-times"></i>
                Close
              </button>
              {orderDetails && (
                <button 
                  className="btn-primary" 
                  onClick={() => {
                    refetch();
                    closeOrderModal();
                  }}
                >
                  <i className="fas fa-sync"></i>
                  Refresh Order
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;